import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useFaceIdentityBase } from '../utils/faceIdentityRoutes';
import FaceIdentityHomeButton from './FaceIdentityHomeButton';
import { useFranchiseFlowAuthActions } from '../../shared/FranchiseFlowAuthContext';
import {
  getActiveCheckinSession,
  SALON_CHECKIN_UPDATED_EVENT,
} from '../../salon/utils/salonFaceSession';
import { finalizeOpenVisitOnLogout } from '../../salon/utils/visitHandoff';
import { getNaturalsSalonModalRoot } from '../../../shared/modalRoots';
import '../../salon/components/SalonHeaderActions.css';
import './FaceIdentityFlowNav.css';

function BackIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/**
 * Shared kiosk nav: back · refresh · home · logout on one row.
 */
export default function FaceIdentityFlowNav({
  onBack,
  onHome,
  onRefresh,
  showBack = true,
  showHome = true,
  showRefresh = true,
  showLogout = true,
  homePath,
  className = '',
}) {
  const navigate = useNavigate();
  const fiBase = useFaceIdentityBase();
  const resolvedHome = homePath || fiBase;
  const franchiseAuth = useFranchiseFlowAuthActions();
  const onLogout = franchiseAuth?.onLogout;
  const logoutBusy = Boolean(franchiseAuth?.logoutBusy);
  const canLogout = showLogout && typeof onLogout === 'function';

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [activeCheckin, setActiveCheckin] = useState(() => getActiveCheckinSession());

  useEffect(() => {
    if (!canLogout) return undefined;
    const sync = () => setActiveCheckin(getActiveCheckinSession());
    sync();
    window.addEventListener(SALON_CHECKIN_UPDATED_EVENT, sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener(SALON_CHECKIN_UPDATED_EVENT, sync);
      window.removeEventListener('focus', sync);
    };
  }, [canLogout]);

  const handleBack = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(resolvedHome, { replace: true });
  }, [navigate, onBack, resolvedHome]);

  const handleRefresh = useCallback(() => {
    if (typeof onRefresh === 'function') {
      onRefresh();
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, [onRefresh]);

  const handleConfirmLogout = async () => {
    if (!canLogout || logoutBusy || logoutSubmitting) return;
    setLogoutSubmitting(true);
    setLogoutError('');
    try {
      await finalizeOpenVisitOnLogout({ complete: false });
      setLogoutOpen(false);
      await onLogout();
    } catch (err) {
      setLogoutError(err?.message || 'Could not sign out');
    } finally {
      setLogoutSubmitting(false);
    }
  };

  const hasOpenVisit =
    activeCheckin?.id &&
    activeCheckin.status !== 'completed' &&
    activeCheckin.status !== 'cancelled';

  return (
    <>
      <div
        className={`fi-flow-nav${canLogout ? ' fi-flow-nav--with-logout' : ''}${className ? ` ${className}` : ''}`.trim()}
      >
        {showBack ? (
          <button
            type="button"
            className="fi-flow-nav__btn fi-flow-nav__btn--back"
            onClick={handleBack}
            aria-label="Go back"
            title="Back"
          >
            <BackIcon />
          </button>
        ) : null}
        {showRefresh ? (
          <button
            type="button"
            className="fi-flow-nav__btn fi-flow-nav__btn--refresh"
            onClick={handleRefresh}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshIcon />
          </button>
        ) : null}
        {showHome ? <FaceIdentityHomeButton onHome={onHome} homePath={homePath} /> : null}
        {canLogout ? (
          <button
            type="button"
            className="fi-flow-nav__btn fi-flow-nav__btn--logout"
            onClick={() => {
              setLogoutError('');
              setLogoutOpen(true);
            }}
            disabled={logoutBusy || logoutSubmitting}
            aria-label={logoutBusy || logoutSubmitting ? 'Signing out' : 'Sign out'}
            title="Sign out"
          >
            <LogoutIcon />
          </button>
        ) : null}
      </div>

      {logoutOpen && canLogout
        ? createPortal(
            <div
              className="salon-logout-modal-overlay"
              role="presentation"
              onClick={() => {
                if (!logoutSubmitting && !logoutBusy) setLogoutOpen(false);
              }}
            >
              <div
                className="salon-logout-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="fi-flow-logout-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="salon-logout-modal-icon" aria-hidden="true">
                  <LogoutIcon />
                </div>
                <h2 id="fi-flow-logout-title" className="salon-logout-modal-title">
                  Sign out?
                </h2>
                <p className="salon-logout-modal-description">
                  {hasOpenVisit
                    ? 'A guest visit is still open on this device. You can finish it after signing in again.'
                    : 'You will need to sign in again to continue.'}
                </p>
                {logoutError ? <p className="salon-logout-modal-error">{logoutError}</p> : null}
                <div className="salon-logout-modal-actions">
                  <button
                    type="button"
                    className="salon-logout-modal-button salon-logout-modal-button-secondary"
                    disabled={logoutSubmitting || logoutBusy}
                    onClick={() => setLogoutOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="salon-logout-modal-button salon-logout-modal-button-primary"
                    disabled={logoutSubmitting || logoutBusy}
                    onClick={handleConfirmLogout}
                  >
                    {logoutSubmitting || logoutBusy ? 'Signing out…' : 'Sign out'}
                  </button>
                </div>
              </div>
            </div>,
            getNaturalsSalonModalRoot(),
          )
        : null}
    </>
  );
}
